import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionExpiredModal from '../common/SessionExpiredModal';
import BookingTimer from './hotel-details/BookingTimer';
import { useBookingSession } from '../../hooks/useBookingSession';
import agentApiService from '../../services/agentApiService';

const AgentPassengerDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL parameters
  const reserveCode = searchParams.get('reserveCode');
  const roomBookingCode = searchParams.get('roomBookingCode');
  const timerKey = searchParams.get('timerKey');

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [roomBookingDisplayResponse, setRoomBookingDisplayResponse] = useState(null);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Use timerKey as expiryTime (timerKey contains the expiry timestamp)
  const expiryTime = timerKey ? parseInt(timerKey, 10) : null;
  const { isExpired, hasValidSession, getTimeRemaining } = useBookingSession(
    expiryTime,
    () => setShowSessionExpiredModal(true) // Show modal when session expires
  );

  // Fetch passenger summary data on component mount
  useEffect(() => {
    fetchPassengerSummary();
  }, []);

  const fetchPassengerSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      const response = await agentApiService.getAllPassengerDetails(
        reserveCode,
        roomBookingCode,
        timerKey
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        setBookingDetails(response.bookingDetails);
        setRoomBookingDisplayResponse(response.roomBookingDisplayResponse);
      } else {
        throw new Error(response.message || 'Failed to fetch passenger summary');
      }
    } catch (error) {
      console.error('Error fetching passenger summary:', error);
      setError(error.message);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to load passenger summary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async (isHoldBooking = false) => {
    // Check session validity before confirmation - show modal if expired
    if (isExpired || !hasValidSession) {
      setShowSessionExpiredModal(true);
      return;
    }

    try {
      setSubmitting(true);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      // Prepare booking details for submission
      const bookingData = {
        agentId: bookingDetails.agentId,
        totalPrice: bookingDetails.totalPrice,
        rooms: bookingDetails.rooms,
        // Add other required fields from bookingDetails
        hotelName: bookingDetails.hotelName,
        address: bookingDetails.address,
        checkInDate: bookingDetails.checkInDate,
        checkOutDate: bookingDetails.checkOutDate,
        checkInTime: bookingDetails.checkInTime || '14:00',
        checkOutTime: bookingDetails.checkOutTime || '12:00',
        city: bookingDetails.city,
        bookingDate: bookingDetails.bookingDate,
        bookingTime: bookingDetails.bookingTime
      };

      const response = await agentApiService.confirmBooking(
        reserveCode,
        roomBookingCode,
        isHoldBooking ? 'true' : 'false',
        bookingDetails.encodedRooms || '',
        bookingData
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        toast.success(response.message || (isHoldBooking ? 'Booking held successfully!' : 'Booking confirmed successfully!'));
        
        // Show success message and close window after delay
        setTimeout(() => {
          window.close();
        }, 2000);
      } else {
        if (response.showModal) {
          setShowInsufficientBalanceModal(true);
        } else {
          throw new Error(response.message || 'Failed to confirm booking');
        }
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to confirm booking: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <LoadingSpinner message="Loading passenger summary..." />
          
          {/* Timeout Progress Indicator */}
          {timeoutProgress > 0 && (
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${
                    showTimeoutWarning ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${timeoutProgress}%` }}
                ></div>
              </div>
              <p className={`text-sm ${showTimeoutWarning ? 'text-yellow-600' : 'text-gray-600'}`}>
                {showTimeoutWarning 
                  ? 'Taking longer than expected... Please wait.'
                  : 'Connecting to prebook service...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Timeout in {Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))} seconds
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Passenger Summary</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchPassengerSummary}
            className="bg-red-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Booking Details Available</h3>
          <p className="text-gray-600">Unable to load booking details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Booking Timer */}
      <BookingTimer timerKey={timerKey} expiryTime={expiryTime} />

      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.close()}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Booking Confirmation</h1>
                <p className="text-sm text-gray-600">Review and confirm your booking</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white">Booking Confirmation</h2>
          </div>

          <div className="p-6">
            {/* Success/Error Messages */}
            {showInsufficientBalanceModal && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">Insufficient Balance</span>
                </div>
                <p className="text-red-700 mt-1">You don't have sufficient balance to complete this booking. Please recharge your wallet.</p>
              </div>
            )}

            {/* Booking Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Booking Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <table className="w-full text-sm">
                  <tbody className="space-y-2">
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Booking Date:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.bookingDate || new Date().toLocaleDateString()}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Booking Time:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.bookingTime || new Date().toLocaleTimeString()}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">City:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.city || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Agent ID:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.agentId}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Hotel Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Hotel Details</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Hotel Name:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.hotelName || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Address:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.address || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Check-In Date:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.checkInDate || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Check-Out Date:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.checkOutDate || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Check-In Time:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.checkInTime || '14:00'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Check-Out Time:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.checkOutTime || '12:00'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Room-wise Passenger Allocation */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Room-wise Passenger Allocation</h3>
              
              {bookingDetails.roomPassengerDetails && bookingDetails.roomPassengerDetails.map((roomDetail, roomIndex) => (
                <div key={roomIndex} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                  {/* Room Header */}
                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                      </svg>
                      <span className="font-semibold text-gray-900">Room #{roomDetail.roomNumber}</span>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Adults Section */}
                    <h4 className="text-md font-semibold text-green-600 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Adults
                    </h4>
                    
                    {roomDetail.adults && roomDetail.adults.map((adult, adultIndex) => (
                      <div key={adultIndex} className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Title:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerTitle}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">First Name:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerFirstName}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Last Name:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerLastName}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            <span className="font-medium text-gray-700">Phone:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerPhoneNumber}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            <span className="font-medium text-gray-700">Email:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerEmail}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Country:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerCountry}</span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Children Section */}
                    <h4 className="text-md font-semibold text-yellow-600 mb-3 flex items-center mt-4">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      Children
                    </h4>
                    
                    {(!roomDetail.children || roomDetail.children.length === 0) ? (
                      <div className="text-gray-500 italic flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        No children in this room
                      </div>
                    ) : (
                      roomDetail.children.map((child, childIndex) => (
                        <div key={childIndex} className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Title:</span>
                              <span className="ml-1 text-gray-900">{child.passengerTitle}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">First Name:</span>
                              <span className="ml-1 text-gray-900">{child.passengerFirstName}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Last Name:</span>
                              <span className="ml-1 text-gray-900">{child.passengerLastName}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Age:</span>
                              <span className="ml-1 text-gray-900">{child.passengerAge}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Room Details Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                </svg>
                Room Details
              </h3>

              {roomBookingDisplayResponse && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {/* Meal Type */}
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                        <div>
                          <span className="font-medium text-gray-700">Meal Type:</span>
                          <div className="font-semibold text-blue-600">
                            {roomBookingDisplayResponse.breakfastText || 'N/A'}
                          </div>
                        </div>
                      </div>

                      {/* Inclusion */}
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium text-gray-700">Inclusion:</span>
                          <div className="font-semibold text-green-600">
                            {roomBookingDisplayResponse.inclusion || 'N/A'}
                          </div>
                        </div>
                      </div>

                      {/* Refundable */}
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium text-gray-700">Refundable:</span>
                          <div className={`font-semibold ${roomBookingDisplayResponse.refundable ? 'text-green-600' : 'text-red-600'}`}>
                            {roomBookingDisplayResponse.refundable ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>

                      {/* With Transfers */}
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                        </svg>
                        <div>
                          <span className="font-medium text-gray-700">With Transfers:</span>
                          <div className={`font-semibold ${roomBookingDisplayResponse.withTransfers ? 'text-green-600' : 'text-red-600'}`}>
                            {roomBookingDisplayResponse.withTransfers ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Promotions */}
                    <div className="mt-4">
                      <div className="flex items-center mb-2">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium text-gray-700">Promotions:</span>
                      </div>
                      {(!roomBookingDisplayResponse.offers || roomBookingDisplayResponse.offers.length === 0) ? (
                        <span className="text-gray-500 italic">None</span>
                      ) : (
                        <ul className="space-y-2">
                          {roomBookingDisplayResponse.offers.map((promo, index) => (
                            <li key={index} className="bg-white border-l-4 border-blue-600 px-3 py-2 rounded shadow-sm">
                              {promo}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Supplements Section */}
            <div className="mb-6">
              <div className="border-0 shadow-lg rounded-lg overflow-hidden bg-white">
                <div className="bg-green-600 text-white font-bold px-4 py-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Supplements
                </div>
                <div className="p-4 bg-gray-50">
                  {(!roomBookingDisplayResponse?.supplements || roomBookingDisplayResponse.supplements.length === 0) ? (
                    <div className="text-gray-500 italic flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      No supplements available.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="border border-gray-300 px-3 py-2 text-left">Type</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Description</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Price</th>
                            <th className="border border-gray-300 px-3 py-2 text-left">Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roomBookingDisplayResponse.supplements.map((supplementList, groupIndex) => (
                            <React.Fragment key={groupIndex}>
                              {/* Group header row */}
                              <tr className="bg-gray-200 font-bold">
                                <td colSpan="4" className="border border-gray-300 px-3 py-2">
                                  Supplement Group {groupIndex + 1}
                                </td>
                              </tr>
                              {/* Individual supplements */}
                              {supplementList.map((supplement, suppIndex) => (
                                <tr key={suppIndex} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 px-3 py-2">{supplement.type}</td>
                                  <td className="border border-gray-300 px-3 py-2">{supplement.description}</td>
                                  <td className="border border-gray-300 px-3 py-2">
                                    <span className="font-bold text-green-600">{supplement.price}</span>
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2">{supplement.currency}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <h3 className="font-bold text-red-600 text-lg flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                  </svg>
                  Cancellation Policy
                </h3>
                <hr className="flex-grow-1 ml-3 border-red-300" />
              </div>

              {(!roomBookingDisplayResponse?.cancelPolicyDTOs || roomBookingDisplayResponse.cancelPolicyDTOs.length === 0) ? (
                <div className="text-gray-500 italic pl-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  No cancellation policies available
                </div>
              ) : (
                roomBookingDisplayResponse.cancelPolicyDTOs.map((policy, policyIndex) => (
                  <div key={policyIndex} className="p-3 mb-3 bg-white border-l-4 border-red-500 shadow-sm rounded-r-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">From:</span>
                          <div className="font-bold text-blue-600 text-sm">{policy.fromDate}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">To:</span>
                          <div className="font-bold text-blue-600 text-sm">{policy.toDate}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">Charge Type:</span>
                          <div className="font-bold text-gray-900 text-sm">{policy.chargeType}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">Charge Value:</span>
                          <div className="font-bold text-green-600 text-lg">{policy.cancellationCharge}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Rate Conditions */}
            <div className="mb-6">
              <div className="border-0 shadow-lg rounded-lg overflow-hidden bg-white">
                <div className="bg-gray-600 text-white font-bold px-4 py-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Rate Conditions
                </div>
                <div className="p-4 bg-gray-50">
                  {(!roomBookingDisplayResponse?.rateConditions || roomBookingDisplayResponse.rateConditions.length === 0) ? (
                    <div className="text-gray-500 italic">
                      No rate conditions available.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {roomBookingDisplayResponse.rateConditions.map((condition, conditionIndex) => (
                        <div key={conditionIndex} className="bg-white border-l-4 border-gray-600 px-3 py-2 rounded-r shadow-sm">
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {/* Check if condition contains HTML tags */}
                            {condition.includes('<') ? (
                              <div dangerouslySetInnerHTML={{ __html: condition }} />
                            ) : (
                              <span>{condition}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* GST Information */}
            {bookingDetails.gstNumber && bookingDetails.gstNumber !== 'NA' && bookingDetails.gstNumber !== '' && (
              <div className="mb-6">
                <div className="border-0 shadow-lg rounded-lg overflow-hidden bg-white">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-4 py-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                    </svg>
                    GST Details
                  </div>
                  <div className="p-4 bg-gray-50 space-y-3">
                    <div className="border-b border-gray-200 pb-2">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold text-gray-700">GST Number:</span>
                        <span className="ml-2 font-semibold text-blue-600">{bookingDetails.gstNumber}</span>
                      </div>
                    </div>
                    
                    {bookingDetails.gstCompanyName && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-1a1 1 0 00-1-1H9a1 1 0 00-1 1v1a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                          </svg>
                          <span className="font-semibold text-gray-700">Company Name:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyName}</span>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyAddress && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-start">
                          <svg className="w-4 h-4 text-red-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <span className="font-semibold text-gray-700">Company Address:</span>
                            <div className="ml-0 font-semibold mt-1">{bookingDetails.gstCompanyAddress}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyContactNumber && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          <span className="font-semibold text-gray-700">Contact Number:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyContactNumber}</span>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyEmail && (
                      <div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          <span className="font-semibold text-gray-700">Email:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyEmail}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price Summary */}
            <div className="mb-6">
              <div className="bg-white shadow-sm border-0 rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white font-bold px-4 py-3">
                  Price Summary
                </div>
                <div className="p-4">
                  <table className="w-full border-collapse">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">Total Price</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-2xl font-bold text-blue-600 flex items-center">
                            <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.totalPriceFormatted || 
                              (bookingDetails.totalPrice ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.totalPrice) : 'N/A')}
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">Platform Fee</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-red-600 font-semibold flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.platformFeeFormatted || 
                              (bookingDetails.platformFee ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.platformFee) : '0')}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">GST Amount</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-yellow-600 font-semibold flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.gstAmountFormatted || 
                              (bookingDetails.gstAmount ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.gstAmount) : '0')}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-6 flex-wrap">
              {/* Confirm Booking */}
              <button
                onClick={() => handleConfirmBooking(false)}
                disabled={submitting || isExpired || !hasValidSession}
                className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                  submitting || isExpired || !hasValidSession
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting ? (
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                    {showTimeoutWarning && (
                      <span className="text-yellow-200 text-xs">
                        ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                      </span>
                    )}
                  </div>
                ) : 'Confirm Booking'}
              </button>

              {/* Hold Booking */}
              {bookingDetails.isRefundable && (
                <button
                  onClick={() => handleConfirmBooking(true)}
                  disabled={submitting || isExpired || !hasValidSession}
                  className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                    submitting || isExpired || !hasValidSession
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-500 text-white hover:bg-yellow-600'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                      {showTimeoutWarning && (
                        <span className="text-yellow-200 text-xs">
                          ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                        </span>
                      )}
                    </div>
                  ) : 'Hold Booking'}
                </button>
              )}

              {/* Cancel */}
              <button
                onClick={() => window.close()}
                disabled={submitting}
                className="px-8 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />
    </div>
  );
};

export default AgentPassengerDetailsPage;